import { slugify } from '@/utility/helpers';
import { Context } from '@nuxt/types';
import { ISbStoryData } from '@storyblok/js';
import {
  transformContactList,
  transformDate,
  transformImage,
  transformLink,
  TransformLinkReturn,
  transformRichText,
  transformVideo,
} from '~/transforms/transformComponents';
import { BusinessLocation } from '~/types/global';
import { ModuleStoryblok, StoryblokCdnV2FilterQuery } from '~/types/modules';
import {
  ArticleStoryblok,
  BenefitItemStoryblok,
  CmAbstractStoryblok,
  CmAccordionStoryblok,
  CmBenefitsStoryblok,
  CmCopyStoryblok,
  CmExpandableLayerStoryblok,
  CmGalleryStoryblok,
  CmStageServiceStoryblok,
  CmStageStoryblok,
  CmTabsStoryblok,
  CmTextImageHighlightStoryblok,
  CmTextMediaStoryblok,
  EmIframeStoryblok,
  EmMapStoryblok,
  FmContactStoryblok,
  LmDownloadsStoryblok,
  LmGridStoryblok,
  LmJobListStoryblok,
  LmSliderStoryblok,
  LocationStoryblok,
  TmGridStoryblok,
  TmHighlightStoryblok,
  TmStandardStoryblok,
} from '~/types/storyblok-generated';

export type TransformedCmAbstract = ReturnType<typeof transformCmAbstract>;
export type TransformedCmAccordion = ReturnType<typeof transformCmAccordion>;
export type TransformedCmBenefits = ReturnType<typeof transformCmBenefits>;
export type TransformedCmCopy = ReturnType<typeof transformCmCopy>;
export type TransformedCmExpandableLayer = ReturnType<typeof transformCmExpandableLayer>;
export type TransformedCmGallery = ReturnType<typeof transformCmGallery>;
export type TransformedCmStage = ReturnType<typeof transformCmStage>;
export type TransformedCmStageService = ReturnType<typeof transformCmStageService>;
export type TransformedCmTabs = ReturnType<typeof transformCmTabs>;
export type TransformedCmTextImageHighlight = ReturnType<typeof transformCmTextImageHighlight>;
export type TransformedCmTextMedia = ReturnType<typeof transformCmTextMedia>;
export type TransformedEmIframe = ReturnType<typeof transformEmIframe>;
export type TransformedEmMap = Awaited<ReturnType<typeof transformEmMap>>;
export type TransformedFmContact = ReturnType<typeof transformFmContact>;
export type TransformedLmDownloads = ReturnType<typeof transformLmDownloads>;
export type TransformedLmGrid = ReturnType<typeof transformLmGrid>;
export type TransformedLmJobList = ReturnType<typeof transformLmJobList>;
export type TransformedLmSlider = Awaited<ReturnType<typeof transformLmSlider>>;
export type TransformedTmGrid = ReturnType<typeof transformTmGrid>;
export type TransformedTmHighlight = ReturnType<typeof transformTmHighlight>;
export type TransformedTmStandard = ReturnType<typeof transformTmStandard>;

export type TransformedModule = (
  | TransformedCmAbstract
  | TransformedCmAccordion
  | TransformedCmBenefits
  | TransformedCmCopy
  | TransformedCmExpandableLayer
  | TransformedCmGallery
  | TransformedCmStage
  | TransformedCmStageService
  | TransformedCmTabs
  | TransformedCmTextImageHighlight
  | TransformedCmTextMedia
  | TransformedEmIframe
  | TransformedEmMap
  | TransformedFmContact
  | TransformedLmDownloads
  | TransformedLmGrid
  | TransformedLmJobList
  | TransformedLmSlider
  | TransformedTmGrid
  | TransformedTmHighlight
  | TransformedTmStandard
) & { _uid: string };

/**
 * Transforms CmAbstract
 */
function transformCmAbstract(moduleData: CmAbstractStoryblok) {
  return {
    type: moduleData.component,
    data: {
      theme: moduleData.theme,
      text: moduleData.text as string | undefined,
      copy: undefined as string | undefined,
      author: moduleData.author ? moduleData.author : undefined,
    },
  };
}

/**
 * Transforms CmAccordion
 */
function transformCmAccordion(moduleData: CmAccordionStoryblok) {
  return {
    type: moduleData.component,
    data: {
      headline: moduleData.headline,
      headlineSize: moduleData.headlineSize,
      description: moduleData.description,
      collapsibles: moduleData.collapsibles?.map((collapsible: any) => {
        const transformedCollapsibleItem = { ...collapsible };
        transformedCollapsibleItem.content = transformRichText(collapsible.content);
        return transformedCollapsibleItem;
      }),
    },
  };
}

/**
 * Transforms CmBenefits
 */
function transformCmBenefits(moduleData: CmBenefitsStoryblok) {
  function transformBenefit(benefit: BenefitItemStoryblok) {
    const benefitItem: {
      headline?: string;
      description?: string;
      number?: number;
      icon?: string;
      link?: TransformLinkReturn;
    } = {};
    if (benefit.number && benefit.number.length > 0) {
      benefitItem.number = parseInt(benefit.number);
    }
    if (benefit.icon && benefit.icon.filename != '') {
      benefitItem.icon = benefit.icon.filename ?? undefined;
    }
    if (benefit.headline) {
      benefitItem.headline = benefit.headline;
    }
    if (benefit.description) {
      benefitItem.description = benefit.description;
    }
    if (benefit.number && benefit.number.length > 0) {
      benefitItem.number = parseInt(benefit.number);
    }
    if (benefit.link && benefit.link.length > 0) {
      benefitItem.link = transformLink(benefit.link[0].link, benefit.link[0].label);
    }
    return benefitItem;
  }

  return {
    type: moduleData.component,
    data: {
      headline: moduleData.headline,
      description: moduleData.description,
      benefits: moduleData.benefits?.map(transformBenefit),
    },
  };
}

/**
 * Transforms CmCopy
 */
function transformCmCopy(moduleData: CmCopyStoryblok) {
  return {
    type: moduleData.component,
    data: {
      moduleHeader: {
        headline: {
          text: moduleData.headline,
          level: 'h2',
          fontStyle: moduleData.fontStyle,
        },
        copy: '',
        button: null,
      },
      logo: transformImage(moduleData.logo),
      copyLeft: transformRichText(moduleData.copyLeft),
      copyRight: transformRichText(moduleData.copyRight),
    },
  };
}

/**
 * Transforms CmExpandableLayer
 */
function transformCmExpandableLayer(moduleData: CmExpandableLayerStoryblok) {
  const transformedGridItems = moduleData.items?.map((item: any) => {
    return {
      headline: {
        text: item.headline,
        level: 'h3',
        fontStyle: 'h5',
      },
      copy: item.copy,
      links: item.links.map((link: any) => ({ label: link.label, layerId: slugify(link.layerId) })),
      layers: item.links.map((layer: any) => {
        return {
          id: slugify(layer.layerId),
          modules: layer.content,
        };
      }),
    };
  });

  return {
    type: moduleData.component,
    data: {
      moduleHeader: {
        headline: {
          text: moduleData.headline,
          level: 'h2',
          fontStyle: 'h2',
        },
        copy: moduleData.copy,
      },
      gridItems: transformedGridItems,
    },
  };
}

/**
 * Transforms CmGallery
 */
function transformCmGallery(moduleData: CmGalleryStoryblok) {
  return {
    type: moduleData.component,
    data: {
      moduleHeader: {
        headline: {
          text: moduleData.headline,
          level: 'h2',
          fontStyle: 'h2',
        },
        button: null,
      },
      heroImage: transformImage(moduleData.heroImage),
      subImage: transformImage(moduleData.subImage),
      highlightText: moduleData.highlightText,
      gallery: moduleData.gallery,
    },
  };
}

/**
 * Transforms CmStage
 */
function transformCmStage(moduleData: CmStageStoryblok) {
  return {
    type: moduleData.component,
    data: {
      variant: moduleData.variant,
      theme: moduleData.background,
      headlineSmall: moduleData.headlineSmall === true,
      headline: {
        level: 'h1',
        fontStyle: moduleData.headlineSmall ? 'h3' : 'h2',
        text: moduleData.headlineText || undefined,
      },
      breadcrumb: transformLink(moduleData.breadcrumbLink, moduleData.breadcrumbLabel),
      copy: moduleData.copy,
      button: transformLink(moduleData.buttonLink, moduleData.buttonLabel),
      image: transformImage(
        moduleData.image,
        moduleData.variant === 'rich' ? '1to1' : undefined,
        undefined,
        'md:50vw 2xl:75vw'
      ),
      video: transformVideo(moduleData.video, '3to2', true, moduleData.image?.filename || ''),
      news: null,
      category: null,
      date: null,
      project: null,
      stageTable: null,
      download: null,
    },
  };
}

/**
 * Transforms CmStageService
 */
function transformCmStageService(moduleData: CmStageServiceStoryblok) {
  let contacts = transformContactList(moduleData.contacts ?? [])?.contacts;
  return {
    type: moduleData.component,
    data: {
      headline: moduleData.headline,
      headlineSize: moduleData.headlineSize,
      copy: moduleData.copy,
      video: transformVideo(moduleData.video, '3to2', true, ''),
      image: transformImage(moduleData.image, '3to2'),
      imageFormat: moduleData.imageFormat,
      buttonLabel: moduleData.label,
      contacts: contacts,
    },
  };
}

/**
 * Transforms CmTabs
 */
function transformCmTabs(moduleData: CmTabsStoryblok) {
  return {
    type: moduleData.component,
    data: {
      tabItems: moduleData.tabsItems.map(tabItem => ({
        title: tabItem.title.replace('&', '<br/>&'),
        text: tabItem.text,
        image1: transformImage(tabItem.image1, '1to1', undefined, 'md:100vw lg:50vw'),
        image2: transformImage(tabItem.image2, undefined, undefined, 'md:100vw lg:50vw'),
        linksDescription: tabItem.linksDescription,
        links: tabItem.links?.map(linkItem => transformLink(linkItem.link, linkItem.label)),
      })),
    },
  };
}

/**
 * Transforms CmTextImageHighlight
 */
function transformCmTextImageHighlight(moduleData: CmTextImageHighlightStoryblok) {
  return {
    type: moduleData.component,
    data: {
      kicker: moduleData.kicker,
      link: transformLink(moduleData.linkUrl, moduleData.linkText),
      copy: moduleData.copy,
      imageHero: transformImage(moduleData.imageHero),
      imageSub: transformImage(moduleData.imageSub),
    },
  };
}

/**
 * Transforms CmTextMedia
 */
function transformCmTextMedia(moduleData: CmTextMediaStoryblok) {
  return {
    type: moduleData.component,
    data: {
      reverse: moduleData.alignment,
      headline: {
        text: moduleData.headlineText,
        level: 'h2',
        fontStyle: moduleData.headlineStyle,
      },
      copy: moduleData.text,
      link: transformLink(moduleData.linkUrl, moduleData.linkText),
      ratio: moduleData.format,
      image: transformImage(moduleData.image),
      vimeoID: moduleData.vimeoID,
    },
  };
}

/**
 * Transforms EmIframe
 */
function transformEmIframe(moduleData: EmIframeStoryblok) {
  return {
    type: moduleData.component,
    data: {
      moduleHeader: {
        headline: {
          text: moduleData.headlineText,
          level: 'h2',
          fontStyle: 'h3',
        },
        copy: transformRichText(moduleData.copy),
        button: transformLink(moduleData.buttonLink, moduleData.buttonLabel),
      },
      src: moduleData.src,
      cookieConsentServiceName: moduleData.cookieConsentServiceName,
    },
  };
}

/**
 * Transforms EmMap
 */
function transformEmMap(moduleData: EmMapStoryblok) {
  const locationsMap = moduleData.locations?.map((locationItem: any) => {
    return {
      ...locationItem.content,
      position: {
        lat: parseFloat(locationItem.content.latitude),
        lng: parseFloat(locationItem.content.longitude),
      },
      isUnique: null,
    } as BusinessLocation;
  });

  return {
    type: moduleData.component,
    data: {
      moduleHeader: {
        headline: {
          text: moduleData.headlineText,
          level: 'h2',
          fontStyle: 'h3',
        },
        copy: moduleData.copy,
      },
      map: {
        locations: locationsMap,
        initialSetup: {
          zoom: moduleData.initialZoom,
          latitude: moduleData.initialLatitude,
          longitude: moduleData.initialLongitude,
        },
      },
    },
  };
}

/**
 * Transforms FmContact
 */
function transformFmContact(moduleData: FmContactStoryblok) {
  return {
    type: moduleData.component,
    data: {
      // If headlineText is empty, we don't want to render the moduleHeader!
      moduleHeader:
        moduleData.headlineText && moduleData.headlineText.length > 1
          ? {
              headline: {
                text: moduleData.headlineText,
                level: 'h2',
                fontStyle: 'h3',
              },
              copy: moduleData.copy,
            }
          : undefined,
      id: moduleData.id,
      noExtraTopSpacing: moduleData.noExtraTopSpacing,
      contactAddress: moduleData.contactAddress,
      buttonLabel: moduleData.buttonLabel,
      marginal: transformRichText(moduleData.marginal),
      contactForm: transformRichText(moduleData.contactForm),
      confirmation: transformRichText(moduleData.confirmation),
    },
  };
}

/**
 * Transforms LmDownloads
 */
function transformLmDownloads(moduleData: LmDownloadsStoryblok) {
  return {
    type: moduleData.component,
    data: {
      moduleHeader: {
        headline: {
          text: moduleData.headline,
          level: 'h2',
          fontStyle: 'h4',
        },
        copy: moduleData.copy,
        button: null,
      },
      downloadSections: moduleData.download_sections,
    },
  };
}

/**
 * Transforms LmGrid
 */
function transformLmGrid(moduleData: LmGridStoryblok) {
  return {
    type: moduleData.component,
    data: {
      moduleHeader: {
        headline: {
          text: moduleData.headlineText,
          level: 'h2',
          fontStyle: 'h2',
        },
        copy: moduleData.copy,
        button: transformLink(moduleData.buttonLink, moduleData.buttonLabel),
      },
      articlePerPage: moduleData.articlePerPage,
      slides: moduleData.article,
      categories: moduleData.categories,
      tags: moduleData.tags,
      subtags: moduleData.subtags,
    },
  };
}

/**
 * Transforms LmJobList
 */
function transformLmJobList(moduleData: LmJobListStoryblok) {
  return {
    type: moduleData.component,
    data: {
      headline: moduleData.headline,
      listquery: moduleData.listquery,
    },
  };
}

/**
 * Transforms LmSlider
 */
async function transformLmSlider(moduleData: LmSliderStoryblok, context: Context) {
  // Load categories data lazily
  const categoriesData = context.$getDataSourceCache('categories') || (await context.$loadDataSource('categories'));
  let queryData: ISbStoryData<ArticleStoryblok>[];
  let slidesBySelection = false as Boolean;
  const langCode = context.route.path.split('/')[1];

  async function getFilteredArticles(
    filter_query: StoryblokCdnV2FilterQuery
  ): Promise<ISbStoryData<ArticleStoryblok>[]> {
    const filterData = await context.app.$storyapi.get(`cdn/stories`, {
      starts_with: `${langCode}/articles`,
      version: process.env.STORYBLOK_CONTENT_VERSION,
      resolve_links: 'url',
      sort_by: 'content.date:desc',
      filter_query: filter_query,
      per_page: '9',
    });
    return filterData.data.stories;
  }

  if (moduleData.category?.length === 0 && (moduleData.tags?.length ?? 0) > 0) {
    queryData = await getFilteredArticles({ tags: { any_in_array: moduleData.tags?.join(',') } });
  } else if ((moduleData.category?.length ?? 0) > 0 && moduleData.tags?.length === 0) {
    queryData = await getFilteredArticles({ category: { in: moduleData.category?.join(',') } });
  } else if ((moduleData.category?.length ?? 0) > 0 && (moduleData.tags?.length ?? 0) > 0) {
    queryData = await getFilteredArticles({
      category: { in: moduleData.category?.join(',') },
      tags: { any_in_array: moduleData.tags?.join(',') },
    });
  } else {
    queryData = moduleData.slides?.filter(article => typeof article !== 'string') ?? [];
    slidesBySelection = true;
  }

  const dataMap =
    queryData.map(dataItem => ({
      category: categoriesData[slugify(dataItem.content.category)]?.value || dataItem.content.category,
      image: transformImage(dataItem.content.image, undefined, undefined, 'md:100vw 2xl:50vw'),
      date: transformDate(dataItem.content.date),
      subtags: dataItem.content.tags,
      link: transformLink({ url: dataItem.full_slug }),
      text: dataItem.content.stageHeadline,
    })) ?? [];

  return {
    type: moduleData.component,
    data: {
      moduleHeader: {
        headline: {
          text: moduleData.headlineText,
          level: 'h2',
          fontStyle: 'h2',
        },
        copy: moduleData.copy,
        button: transformLink(moduleData.buttonLink, moduleData.buttonLabel),
      },
      slidesBySelection: slidesBySelection,
      slides: dataMap,
    },
  };
}

/**
 * Transforms TmGrid
 */
function transformTmGrid(moduleData: TmGridStoryblok) {
  return {
    type: moduleData.component,
    data: {
      moduleHeader: {
        headline: {
          text: moduleData.headline,
          level: 'h2',
          fontStyle: 'h2',
        },
        copy: moduleData.copy,
      },
      teaser_rows: moduleData.teaser_rows,
    },
  };
}

/**
 * Transforms TmHighlight
 */
function transformTmHighlight(moduleData: TmHighlightStoryblok) {
  return {
    type: moduleData.component,
    data: {
      copy: moduleData.copy,
      button: transformLink(moduleData.buttonLink, moduleData.buttonLabel),
      image: transformImage(moduleData.image, moduleData.imageVariant === '1' ? '1to1' : '4to3'),
      highlightText: moduleData.highlight,
    },
  };
}

/**
 * Transforms TmStandard
 */
function transformTmStandard(moduleData: TmStandardStoryblok, context: Context) {
  let formattedDate = null;
  if (moduleData.date) {
    const { lang } = context.i18n.localeProperties;
    const dateOptions: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    };
    // Intl.DateTimeFormat not supported on iOS < 16 ???
    // formattedDate = new Intl.DateTimeFormat('de-DE', dateOptions).format(new Date(moduleData.date))
    formattedDate = new Date(moduleData.date).toLocaleString(lang, dateOptions);
  }
  return {
    type: moduleData.component,
    data: {
      headline: {
        text: moduleData.headlineText,
        level: 'h2',
        fontStyle: 'h3',
      },
      date: formattedDate,
      location: moduleData.location,
      topic: moduleData.topic,
      copy: moduleData.copy,
      button:
        moduleData.buttonLink && moduleData.buttonLabel
          ? transformLink(moduleData.buttonLink, moduleData.buttonLabel)
          : null,
    },
  };
}

export default async function transformModules(modules: any, context: Context): Promise<any[] | void> {
  if (!modules) return [];

  // Add module function here
  async function transformModule(storyblokData: ModuleStoryblok) {
    try {
      switch (storyblokData.component) {
        case 'cm-text-media':
          return transformCmTextMedia(storyblokData);
        case 'cm-text-image-highlight':
          return transformCmTextImageHighlight(storyblokData);
        case 'cm-stage':
          return transformCmStage(storyblokData);
        case 'cm-stage-service':
          return transformCmStageService(storyblokData);
        case 'cm-copy':
          return transformCmCopy(storyblokData);
        case 'lm-downloads':
          return transformLmDownloads(storyblokData);
        case 'cm-abstract':
          return transformCmAbstract(storyblokData);
        case 'cm-benefits':
          return transformCmBenefits(storyblokData);
        case 'cm-tabs':
          return transformCmTabs(storyblokData);
        case 'lm-slider':
          return await transformLmSlider(storyblokData, context);
        case 'lm-grid':
          return transformLmGrid(storyblokData);
        case 'tm-standard':
          return transformTmStandard(storyblokData, context);
        case 'tm-grid':
          return transformTmGrid(storyblokData);
        case 'tm-highlight':
          return transformTmHighlight(storyblokData);
        case 'cm-accordion':
          return transformCmAccordion(storyblokData);
        case 'cm-gallery':
          return transformCmGallery(storyblokData);
        case 'em-map':
          return transformEmMap(storyblokData);
        case 'em-iframe':
          return transformEmIframe(storyblokData);
        case 'cm-expandable-layer':
          return transformCmExpandableLayer(storyblokData);
        case 'fm-contact':
          return transformFmContact(storyblokData);
        case 'lm-job-list':
          return transformLmJobList(storyblokData);
        default:
          return transfromModuleError('No transform rule found.', storyblokData);
      }
    } catch (error) {
      return transfromModuleError(error, storyblokData);
    }
  }

  function transfromModuleError(error: any, moduleData: ModuleStoryblok) {
    return {
      type: 'TransformError',
      data: {
        headline: `Error for module: ${moduleData.component}`,
        errorDescription: error.stack || error,
      },
    };
  }

  if (!modules.length) {
    return [];
  }

  return await Promise.all(
    modules.map(async (module: ModuleStoryblok) => {
      return {
        uid: module._uid,
        ...(await transformModule(module)),
        _editable: process.env.ENVIRONMENT !== 'production' ? (module as any)._editable : undefined,
      };
    })
  );
}
